import React, { FC, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import TaskType from "../../Shared/TaskType";
import TaskPriority from "../../Shared/TaskPriority";
import "./BoardTask.css";
import { ProgressBar } from "primereact/progressbar";
import BtnMore from "./BtnMore";
import { Context } from "../../..";
import ConfirmationWindow from "../../Shared/ConfirmationWindow/ConfirmationWindow";
import TaskAddEditForm from "../../Task/TaskAddEditForm/TaskAddEditForm";
import { Link } from "react-router-dom";
import { AppContext } from "../../../App";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import Tooltip from "../../UiLib/CustomTooltip/CustomTooltip";
import BoardFrame from "../../../assets/board_frame.svg";
import { hasPermission } from "../../../helpers/hasPermisson";
import { getOptionsQueryParams } from "../../../helpers/paramsAutoFiller";
import { fullNameFormat } from "../../../helpers/Inicials";
import PersonLineDisplay from "../../Shared/PersonLineDisplay";
import { BoardContext } from "../../../contexts/BoardContext";
import { IBoardTaskDisplay } from "../../../common/api_interfaces/column/IColumnDisplay";
import { IRestrictBoardView } from "../../../common/api_interfaces/board/IRestrictBoardView";
import { dateToNiceString } from "../../../helpers/dateToNiceString";

const TagsContainer = React.lazy(() => import('../../Shared/Tags/TagsContainer'));

interface IBoardTaskProps {
    task: IBoardTaskDisplay;
    onTaskDragEnd: () => void;
    isSelectable: boolean;
    isSelected: boolean;
    userAccess: IFunctionPermissions[];
    columnAccess: boolean;
    boardsData?: IRestrictBoardView | undefined;
    currentZoom: number;
    onLoadingChange?: (loading: boolean) => void;
    editTask: boolean;
    availableColumns?: any[];
}

const BoardTask: FC<IBoardTaskProps> = React.memo(({
    task,
    onTaskDragEnd,
    isSelectable,
    isSelected,
    userAccess,
    columnAccess,
    boardsData,
    currentZoom,
    onLoadingChange,
    editTask,
    availableColumns
}) => {
    const boardContext = useContext(BoardContext);

    const { store } = useContext(Context);
    const { archivedPage } = useContext(AppContext);

    const dataForEdit = useMemo(() => ["Переместить", "Редактировать", "Удалить"], []);
    const [open, setOpen] = useState(false);
    const [showEditForm, setShowEditForm] = useState<boolean>(false);
    const [openConfirmWindow, setOpenConfirmWindow] = useState(false);

    const END_STATUS = "Завершено";


    const taskDesc = useMemo(() =>
        task?.description?.replace(/<[^>]*>/g, ' ')?.replace(/&nbsp;/g, ' ') ?? "",
        [task.description]);

    const responsible = task.responsible;
    const responsibleInitials = useMemo(() =>
        responsible ? fullNameFormat(
            {
                name: responsible.name,
                surname: responsible.surname,
                middlename: responsible.middlename,
            },
            "s N M"
        ) : "",
        [responsible]);

    const onDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        if (!store.isDrag || !columnAccess) return;

        const target = e.currentTarget;
        const clone = target.cloneNode(true) as HTMLElement;

        clone.childNodes[1]?.childNodes[1]?.remove();
        clone.style.position = "absolute";
        clone.style.top = "-9999px";
        clone.style.left = "-9999px";

        document.body.appendChild(clone);

        const rect = target.getBoundingClientRect();
        e.dataTransfer.setDragImage(clone, rect.width / 2, rect.height / 2);

        setTimeout(() => {
            document.body.removeChild(clone);
            target.classList.add("drag-hidden");
        }, 0);

        e.dataTransfer.setData("application/json", JSON.stringify({ taskId: task.id }));
    }, [store.isDrag, columnAccess, task.id]);

    const onDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        (e.target as HTMLElement).classList.remove("drag-hidden");
        onTaskDragEnd();
    }, [onTaskDragEnd]);

    const taskPriorityDisplay = useMemo(() => {
        const styleItem = JSON.parse(task.priority.color);
        return <TaskPriority name={task.priority.name} color={styleItem.color} icon={styleItem.icon} />;
    }, [task.priority]);

    const taskTypeDisplay = useMemo(() => {
        const styleItem = JSON.parse(task.type.color);
        return <TaskType name={task.type.name} color={styleItem.color} icon={styleItem.icon} />;
    }, [task.type]);

    const onEditClick = useCallback((e: React.MouseEvent) => {
        setOpen(false);
        const action = (e.target as HTMLElement).innerText;

        switch (action) {
            case "Редактировать":
                store.setDrag(false);
                setShowEditForm(true);
                break;
            case "Удалить":
                setOpenConfirmWindow(true);
                break;
            default:
                break;
        }
    }, [store]);

    const onFormClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
    }, []);

    const onEditFormClose = useCallback(() => {
        store.setDrag(true);
        setShowEditForm(false);
    }, [store]);

    const onConfirm = useCallback(() => {
        boardContext.handleDeleteTask(task.id);
        setOpenConfirmWindow(false);
    }, [boardContext.handleDeleteTask, task.id]);

    const onCancel = useCallback(() => {
        setOpenConfirmWindow(false);
    }, []);

    useEffect(() => {
        const options = getOptionsQueryParams(window.location.search);
        if (
            options.page === "board" &&
            options.entity === "task-form" &&
            options.action === "edit" &&
            options.entityId === task.id
        ) {
            store.setDrag(false);
            setShowEditForm(true);
        }
    }, [task.id, store]);

    const handleCheckboxChange = useCallback(() => {
        boardContext.handleSelectTask(task.id);
        boardContext.handleSelectTaskToColumn(task.taskToColumnId);
    }, [boardContext.handleSelectTask, boardContext.handleSelectTaskToColumn, task.id, task.taskToColumnId]);

    const [isTagsLoading, setIsTagsLoading] = useState(false);

    useEffect(() => {
        if (onLoadingChange && !isTagsLoading) {
            onLoadingChange(isTagsLoading);
        }
    }, [isTagsLoading, onLoadingChange]);

    const priorityColor = useMemo(() => {
        try {
            const styleItem = JSON.parse(task.priority.color);
            return styleItem.color || '#ccc'; // fallback color
        } catch (e) {
            return '#ccc';
        }
    }, [task.priority.color]);

    const getDeadlineColor = (dateEnd: string | undefined): string => {
        if (task.status.name === END_STATUS) {
            return "";
        }

        if (!task.endDate) return 'var(--base-branded-gray-800)';

        try {
            const deadline = new Date(task.endDate);
            const now = new Date();
            const timeDiff = deadline.getTime() - now.getTime();
            const daysDiff = timeDiff / (1000 * 3600 * 24);

            if (daysDiff > 3) {
                return '#52c41a';
            } else if (daysDiff >= 1) {
                return '#faad14';
            } else if (daysDiff >= 0) {
                return '#ff4d4f';
            } else {
                return '#f5222d';
            }
        } catch (error) {
            console.error('Error parsing date:', error);
            return 'var(--base-branded-gray-800)';
        }
    };


    return (
        <div
            id={`task-${task.id}`}
            className="board-task-card"
            draggable={store.isDrag && columnAccess}
            onDragStart={store.isDrag && columnAccess ? onDragStart : undefined}
            onDragEnd={store.isDrag && columnAccess ? onDragEnd : undefined}
        >
            {openConfirmWindow && (
                <ConfirmationWindow
                    headerText="Удаление задачи"
                    bodyText="Вы уверены, что хотите удалить задачу?"
                    confirmText="Да"
                    cancelText="Нет"
                    onConfirm={onConfirm}
                    onCancel={onCancel}
                />
            )}

            {showEditForm && (
                <div className="full-screen-overlay" onClick={onEditFormClose}>
                    <div
                        className="popup-form-background"
                        style={{ zoom: `${1 / Number(currentZoom)}` }}
                        onClick={onFormClick}
                    >
                        <TaskAddEditForm
                            responsibleRequired={boardContext.projectSettings.responsibleRequired}
                            mode="edit"
                            taskId={task.id}
                            closeFunc={onEditFormClose}
                            columnId={task.columnPosition}
                            onTaskAction={boardContext.handleTaskAction}
                            permission={editTask}
                            optionsQueryParams={{
                                page: "board",
                                entity: "task-form",
                                action: "edit",
                                entityId: task.id,
                            }}
                        />
                    </div>
                </div>
            )}

            <div className="dash__board--form-column__tasks--elem__header">
                <div className="dash__board--form-column__tasks--elem__header__shortDescription">
                    <div className="task-abbr-container">
                        <Tooltip text={task.priority.name}>
                            <div
                                className="board-task-priority-bar"
                                style={{ backgroundColor: priorityColor }}
                            >

                            </div>
                        </Tooltip>
                        <div className="task__abbr__board">
                            <div>{task.idForView}</div>
                            {task.parentTaskId && (
                                <div className="task-abbr-img">
                                    <img src={BoardFrame} alt="board-frame" />
                                </div>
                            )}
                        </div>
                        <div className="task-status-board">
                            <p style={{ color: `${JSON.parse(task.status?.style).color}` }}>
                                {task.status?.name}
                            </p>
                        </div>
                        <div className="task__short--description__button">
                            <BtnMore
                                onClickFunc={onEditClick}
                                data={dataForEdit}
                                typeForKey="nameProj"
                                open={open}
                                setOpen={setOpen}
                                columnsChoices={availableColumns}
                                boardChoices={boardContext.boardsForProxy}
                                taskId={task.id}
                                userAccess={userAccess}
                                columnAccess={columnAccess}
                                boardsData={boardsData}
                            />
                        </div>
                    </div>
                    <div className="task__name__board">
                        {isSelectable && (
                            <input
                                className="board-task-checkbox"
                                onChange={handleCheckboxChange}
                                value={task.id}
                                type="checkbox"
                                checked={isSelected}
                            />
                        )}
                        <Tooltip
                            text={task.name.length > 36 ? task.name : ""}
                            offset={5}
                        >
                            <Link
                                draggable={false}
                                className="task__name__board--new"
                                to={`${archivedPage ? "/archive" : ""}/board/${boardContext.boardIdNumber}/task/${task.id}`}
                            >
                                {task.name}
                            </Link>
                        </Tooltip>
                    </div>
                    <div className="task--description__board">
                        <Tooltip text={taskDesc}>
                            <p>{taskDesc.slice(0, 200)}</p>
                        </Tooltip>
                    </div>
                </div>
            </div>
            <div className="dash__board--form-column__tasks__menu">
                <div className="dash__board--form-column__tasks__priority--list">
                    <div className="dash__board--form-column__tasks__priority--list__item">
                        {taskTypeDisplay}
                    </div>
                    {task.endDate ?
                        <div className="task--date__board">
                            <Tooltip text={"Дата окончания"}>
                                <span style={{
                                    color: getDeadlineColor(task.endDate)
                                }}>{dateToNiceString(new Date(task.endDate), 'dd.MM.yyyy')}</span>
                            </Tooltip>
                        </div> :
                        null}
                    {/* {taskPriorityDisplay} */}
                </div>
                {task.tags.length > 0 && <div className="dash-board-task-current-tags-container">
                    <React.Suspense
                        fallback={
                            <div className='board-task-lazy-tags'>
                                <div className='board-task-lazy-tag'></div>
                                <div className='board-task-lazy-tag'></div>
                                <div className='board-task-lazy-tag'></div>
                            </div>
                        }
                    >
                        <TagsContainer tags={task.tags} onLoadingChange={(loading) => { setIsTagsLoading(loading) }} />
                    </React.Suspense>
                </div>
                }
            </div>
            <div className="dash__board--form-column__tasks--elem__bottom">
                <div className="dash__board--form-column__tasks--elem__bottom--flex">
                    {responsible && (
                        <div className="dash__board--form-column__tasks--elem__bottom--name">
                            <PersonLineDisplay
                                name={responsibleInitials}
                                userId={responsible.id}
                            />
                        </div>
                    )}
                    <div className="board-task-progressbar">
                        <div className="board-task-progressbar-text">
                            {task.percentageCompletion}%
                        </div>
                        <div className="board-task-progressbar-line">
                            <div
                                style={{
                                    width: `${task.percentageCompletion}%`
                                }}
                                className="board-task-progressbar-line-color"
                            >

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default BoardTask;